if (!customElements.get('parts-model-pdp')) {
  class PartsModelPdp extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.hasInitialized = false;
      this.lastActiveElement = null;
      this.modalCssVariableNames = [
        '--parts-pdp-bg',
        '--parts-pdp-text',
        '--parts-pdp-muted',
        '--parts-pdp-heading',
        '--parts-pdp-border',
        '--parts-pdp-border-strong',
        '--parts-pdp-subtle',
        '--parts-pdp-hover',
        '--parts-pdp-wash',
        '--parts-pdp-focus-ring',
        '--parts-pdp-accent',
        '--parts-pdp-accent-soft',
        '--parts-pdp-modal-z-index',
        '--parts-header-radius',
        '--parts-header-media-radius',
        '--spacing-unit'
      ];

      this.pointerState = {
        isPointerDown: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        startPanX: 0,
        startPanY: 0,
        moved: false
      };

    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handlePointerCancel = this.handlePointerCancel.bind(this);

    this.state = {
      activeViewMode: 'views',
      activeViewIndex: 0,
      selectedMarkerNumber: '',
      selectedPartSku: '',
      zoom: 1,
      partsSearch: '',
      panX: 0,
      panY: 0,
      expandedAlternateGroups: {}
    };

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleModalClick = this.handleModalClick.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      if (this.hasInitialized) return;
      this.hasInitialized = true;

      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this), 300);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      window.removeEventListener('on:breakpoint-change', this.handleBreakpointChange);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('input', this.handleInput);
      this.removeEventListener('change', this.handleChange);
      document.removeEventListener('keydown', this.handleKeydown);
      this.unlockBodyScroll();
      this.cleanupPortaledModals();
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.removeEventListener('pointermove', this.handlePointerMove);
      this.removeEventListener('pointerup', this.handlePointerUp);
      this.removeEventListener('pointercancel', this.handlePointerCancel);
    }

    init() {
      this.shell = this.querySelector('.parts-pdp__shell');

      this.settings = {
        lang: this.dataset.language || 'en',
        endpointBase: this.dataset.endpointBase || '/apps/greenworks/parts',
        productUrlPrefix: this.dataset.productUrlPrefix || '/products/',
        showBreadcrumbs: this.dataset.showBreadcrumbs === 'true',
        relatedDocumentsLabel: this.dataset.relatedDocumentsLabel || 'Related Documents',
        viewProductDetailsLabel: this.dataset.viewProductDetailsLabel || 'View Product Details',
        productInformationLabel: this.dataset.productInformationLabel || 'Product Information',
        breadcrumbHomeLabel: this.dataset.breadcrumbHomeLabel || 'Home',
        loadingLabel: this.dataset.loadingLabel || 'Loading parts model information',
        errorLabel: this.dataset.errorLabel || 'We couldn’t load this parts model right now.',
        missingSkuLabel: this.dataset.missingSkuLabel || 'No SKU was found in the page URL.',
        noDocumentsLabel: this.dataset.noDocumentsLabel || 'No related documents available.',
        skuLabel: this.dataset.skuLabel || 'SKU',
        modelLabel: this.dataset.modelLabel || 'MODEL',
        documentsModalTitle: this.dataset.documentsModalTitle || 'Related Documents',
        documentsOpenLabel: this.dataset.documentsOpenLabel || 'Open document',
        documentsCloseLabel: this.dataset.documentsCloseLabel || 'Close',
        productInfoModalTitle: this.dataset.productInfoModalTitle || 'Product Information',
        productInfoOpenLabel: this.dataset.productInfoOpenLabel || 'Open product information',
        productInfoCloseLabel: this.dataset.productInfoCloseLabel || 'Close',
        keyFeaturesHeading: this.dataset.keyFeaturesHeading || 'Key Features',
        specificationsHeading: this.dataset.specificationsHeading || 'Specifications',
        noKeyFeaturesLabel: this.dataset.noKeyFeaturesLabel || 'No key features available.',
        noSpecificationsLabel: this.dataset.noSpecificationsLabel || 'No specifications available.',
        helpLabel: this.dataset.helpLabel || 'How to use Parts Finder',
        helpModalTitle: this.dataset.helpModalTitle || 'How to Use the Parts Finder',
        helpCloseLabel: this.dataset.helpCloseLabel || 'Close',

        diagramHeading: this.dataset.diagramHeading || 'Diagram',
        diagramViewsLabel: this.dataset.diagramViewsLabel || 'Views',
        diagramCoverImageLabel: this.dataset.diagramCoverImageLabel || 'Cover Image',
        diagramPreviousLabel: this.dataset.diagramPreviousLabel || 'Previous view',
        diagramNextLabel: this.dataset.diagramNextLabel || 'Next view',
        diagramZoomInLabel: this.dataset.diagramZoomInLabel || 'Zoom in',
        diagramZoomOutLabel: this.dataset.diagramZoomOutLabel || 'Zoom out',
        diagramResetZoomLabel: this.dataset.diagramResetZoomLabel || 'Reset zoom',
        diagramNoViewsLabel: this.dataset.diagramNoViewsLabel || 'No exploded views available.',
        diagramNoImageLabel: this.dataset.diagramNoImageLabel || 'No diagram image available.',
        diagramHintLabel: this.dataset.diagramHintLabel || 'Select a marker to highlight the matching part below.',
        diagramPartsCountLabel: this.dataset.diagramPartsCountLabel || 'Parts',
        diagramPurchasableCountLabel: this.dataset.diagramPurchasableCountLabel || 'Purchasable',

        partsHeading: this.dataset.partsHeading || 'Parts List',
        partsSearchPlaceholder: this.dataset.partsSearchPlaceholder || 'Search by part name, SKU or marker',
        partsEmptyLabel: this.dataset.partsEmptyLabel || 'No parts are available for this view.',
        partsEmptySearchLabel: this.dataset.partsEmptySearchLabel || 'No parts matched your search.',
        partsResultsLabel: this.dataset.partsResultsLabel || 'results',
        partsImageLabel: this.dataset.partsImageLabel || 'Image',
        partsMarkerLabel: this.dataset.partsMarkerLabel || 'Item',
        partsPartNameLabel: this.dataset.partsPartNameLabel || 'Part Name',
        partsSkuLabel: this.dataset.partsSkuLabel || 'Part Number',
        partsPriceLabel: this.dataset.partsPriceLabel || 'Price',
        partsAvailabilityLabel: this.dataset.partsAvailabilityLabel || 'Availability',
        partsActionLabel: this.dataset.partsActionLabel || 'Action',
        partsViewPartLabel: this.dataset.partsViewPartLabel || 'View Part',
        partsUnavailableLabel: this.dataset.partsUnavailableLabel || 'Unavailable',
        partsInStockLabel: this.dataset.partsInStockLabel || 'In stock',
        partsOutOfStockLabel: this.dataset.partsOutOfStockLabel || 'Out of stock',
        partsNotSoldLabel: this.dataset.partsNotSoldLabel || 'Not sold online',
        partsClearSearchLabel: this.dataset.partsClearSearchLabel || 'Clear',
        partsImageUnavailableLabel: this.dataset.partsImageUnavailableLabel || 'Image Unavailable',

        partsAddToCartLabel: this.dataset.partsAddToCartLabel || 'Add to cart',
        partsAddingToCartLabel: this.dataset.partsAddingToCartLabel || 'Adding...',
        partsAddedToCartLabel: this.dataset.partsAddedToCartLabel || 'Added',
        partsAddToCartErrorLabel: this.dataset.partsAddToCartErrorLabel || 'Unable to add this part to cart right now.',
      };

      this.sku = this.getSkuFromUrl();

      this.addEventListener('click', this.handleClick);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('change', this.handleChange);
      window.addEventListener('on:breakpoint-change', this.handleBreakpointChange);

      this.addEventListener('pointerdown', this.handlePointerDown);
      this.addEventListener('pointermove', this.handlePointerMove);
      this.addEventListener('pointerup', this.handlePointerUp);
      this.addEventListener('pointercancel', this.handlePointerCancel);

      if (!this.sku) {
        this.renderError(this.settings.missingSkuLabel);
        return;
      }

      this.fetchModel();
    }

    handleBreakpointChange() {
      if (!this.data) return;

      if (this.state.zoom !== 1 && window.theme?.mediaMatches?.md === false) {
        this.state.zoom = 1;
        this.renderDiagram();
      }
    }

  handleClick(event) {
  const openButton = event.target.closest('[data-action="open-documents-modal"]');
  if (openButton) {
    event.preventDefault();
    this.openDocumentsModal();
    return;
  }

  const closeButton = event.target.closest('[data-action="close-documents-modal"]');
  if (closeButton) {
    event.preventDefault();
    this.closeDocumentsModal();
    return;
  }

  const overlay = event.target.closest('[data-documents-overlay]');
  if (overlay) {
    event.preventDefault();
    this.closeDocumentsModal();
    return;
  }

  const openProductInfoButton = event.target.closest('[data-action="open-product-info-modal"]');
if (openProductInfoButton) {
  event.preventDefault();
  this.openProductInfoModal();
  return;
}

const closeProductInfoButton = event.target.closest('[data-action="close-product-info-modal"]');
if (closeProductInfoButton) {
  event.preventDefault();
  this.closeProductInfoModal();
  return;
}

const productInfoOverlay = event.target.closest('[data-product-info-overlay]');
if (productInfoOverlay) {
  event.preventDefault();
  this.closeProductInfoModal();
  return;
}

  const openHelpButton = event.target.closest('[data-action="open-help-modal"]');
  if (openHelpButton) {
    event.preventDefault();
    this.openHelpModal();
    return;
  }

  const closeHelpButton = event.target.closest('[data-action="close-help-modal"]');
  if (closeHelpButton) {
    event.preventDefault();
    this.closeHelpModal();
    return;
  }

  const helpOverlay = event.target.closest('[data-help-overlay]');
  if (helpOverlay) {
    event.preventDefault();
    this.closeHelpModal();
    return;
  }

  const modeButton = event.target.closest('[data-action="set-view-mode"]');
  if (modeButton) {
    event.preventDefault();
    this.setActiveViewMode(modeButton.dataset.viewMode);
    return;
  }

  const prevButton = event.target.closest('[data-action="previous-view"]');
  if (prevButton) {
    event.preventDefault();
    this.goToRelativeView(-1);
    return;
  }

  const nextButton = event.target.closest('[data-action="next-view"]');
  if (nextButton) {
    event.preventDefault();
    this.goToRelativeView(1);
    return;
  }

const markerButton = event.target.closest('[data-action="select-marker"]');
if (markerButton) {
  if (this.pointerState.moved) {
    this.pointerState.moved = false;
    return;
  }

  event.preventDefault();
  this.selectMarker(
    markerButton.dataset.markerNumber || '',
    markerButton.dataset.partSku || ''
  );
  return;
}

  const zoomInButton = event.target.closest('[data-action="zoom-in"]');
  if (zoomInButton) {
    event.preventDefault();
    this.setZoom(this.state.zoom + 0.2);
    return;
  }

  const zoomOutButton = event.target.closest('[data-action="zoom-out"]');
  if (zoomOutButton) {
    event.preventDefault();
    this.setZoom(this.state.zoom - 0.2);
    return;
  }

  const resetZoomButton = event.target.closest('[data-action="zoom-reset"]');
  if (resetZoomButton) {
    event.preventDefault();
    this.setZoom(1);
    return;
  }

  const addToCartButton = event.target.closest('[data-action="add-part-to-cart"]');
  if (addToCartButton) {
    event.preventDefault();
    event.stopPropagation();

    const partSku = addToCartButton.dataset.partSku || '';
    const part = this.findPartBySku(partSku);

    if (part) {
      this.addPartToCart(part, addToCartButton);
    }

    return;
  }

  const partRowTrigger = event.target.closest('[data-action="select-part-row"]');
  if (partRowTrigger) {
    const markerNumber = partRowTrigger.dataset.markerNumber || '';
    const partSku = partRowTrigger.dataset.partSku || '';

    if (markerNumber || partSku) {
      event.preventDefault();
      this.selectMarker(markerNumber, partSku, { fromTable: true });
    }
    return;
  }

  const toggleAlternatePartsButton = event.target.closest('[data-action="toggle-alternate-parts"]');
if (toggleAlternatePartsButton) {
  event.preventDefault();

  const parentSku = toggleAlternatePartsButton.dataset.parentSku || '';
  if (parentSku) {
    this.toggleAlternateParts(parentSku);
  }
  return;
}

  const clearSearchButton = event.target.closest('[data-action="clear-parts-search"]');
  if (clearSearchButton) {
    event.preventDefault();
    this.state.partsSearch = '';
    this.renderPartsTable({ preserveFocus: true, scrollToActive: false });

    const input = this.querySelector('[data-action="filter-parts"]');
    if (input) input.focus();
    return;
  }

  const clickedInsideStickySelectionTarget =
    event.target.closest('[data-action="select-marker"]') ||
    event.target.closest('[data-action="select-part-row"]');

  if (!clickedInsideStickySelectionTarget && (this.state.selectedMarkerNumber || this.state.selectedPartSku)) {
    this.clearSelectedMarker();
  }
}

    handleModalClick(event) {
      const closeDocumentsButton = event.target.closest('[data-action="close-documents-modal"]');
      const documentsOverlay = event.target.closest('[data-documents-overlay]');
      if (closeDocumentsButton || documentsOverlay) {
        event.preventDefault();
        this.closeDocumentsModal();
        return;
      }

      const closeProductInfoButton = event.target.closest('[data-action="close-product-info-modal"]');
      const productInfoOverlay = event.target.closest('[data-product-info-overlay]');
      if (closeProductInfoButton || productInfoOverlay) {
        event.preventDefault();
        this.closeProductInfoModal();
        return;
      }

      const closeHelpButton = event.target.closest('[data-action="close-help-modal"]');
      const helpOverlay = event.target.closest('[data-help-overlay]');
      if (closeHelpButton || helpOverlay) {
        event.preventDefault();
        this.closeHelpModal();
      }
    }

    handleInput(event) {
      const searchInput = event.target.closest('[data-action="filter-parts"]');
      if (!searchInput) return;

      this.state.partsSearch = searchInput.value || '';
      this.renderPartsTable({
        preserveFocus: true,
        focusValue: this.state.partsSearch,
        selectionStart: searchInput.selectionStart,
        selectionEnd: searchInput.selectionEnd,
        scrollToActive: false
      });
    }

    handleChange(event) {
  const viewSelect = event.target.closest('[data-action="select-view-index"]');
  if (viewSelect) {
    const nextIndex = Number(viewSelect.value);
    if (Number.isFinite(nextIndex)) {
      this.setActiveViewIndex(nextIndex);
    }
    return;
  }
}

    handleKeydown(event) {
  if (event.key !== 'Escape') return;

  if (this.isHelpModalOpen()) {
    this.closeHelpModal();
    return;
  }

  if (this.isProductInfoModalOpen()) {
    this.closeProductInfoModal();
    return;
  }

  if (this.isDocumentsModalOpen()) {
    this.closeDocumentsModal();
  }
}

    getDocumentsModal() {
      return document.getElementById(`PartsPdpDocumentsModal-${this.dataset.sectionId}`);
    }

    getProductInfoModal() {
      return document.getElementById(`PartsPdpProductInfoModal-${this.dataset.sectionId}`);
    }

    getHelpModal() {
      return document.getElementById(`PartsPdpHelpModal-${this.dataset.sectionId}`);
    }

    syncModalCssVariables(modal) {
      if (!modal || typeof window === 'undefined' || !window.getComputedStyle) return;

      const componentStyles = window.getComputedStyle(this);
      this.modalCssVariableNames.forEach((variableName) => {
        const variableValue = componentStyles.getPropertyValue(variableName);
        if (variableValue) {
          modal.style.setProperty(variableName, variableValue.trim());
        }
      });
    }

    portalModal(modal) {
      if (!modal || typeof document === 'undefined' || !document.body) return;

      this.syncModalCssVariables(modal);
      modal.dataset.partsPdpPortal = this.dataset.sectionId || '';
      modal.addEventListener('click', this.handleModalClick);

      if (modal.parentNode !== document.body) {
        document.body.appendChild(modal);
      }
    }

    restoreModal(modal) {
      if (!modal) return;

      modal.removeEventListener('click', this.handleModalClick);
      delete modal.dataset.partsPdpPortal;

      if (this.shell && this.isConnected && modal.parentNode === document.body) {
        this.shell.appendChild(modal);
        return;
      }

      if (modal.parentNode === document.body) {
        modal.remove();
      }
    }

    cleanupPortaledModals() {
      const sectionId = this.dataset.sectionId || '';
      const modals = Array.from(document.querySelectorAll('.parts-pdp__modal[data-parts-pdp-portal]'))
        .filter((modal) => modal.dataset.partsPdpPortal === sectionId);

      if (!modals.length) return;

      document.removeEventListener('keydown', this.handleKeydown);
      this.unlockBodyScroll();

      modals.forEach((modal) => {
        modal.removeEventListener('click', this.handleModalClick);
        if (modal.parentNode === document.body) {
          modal.remove();
        }
      });
    }

    getSkuFromUrl() {
      try {
        const url = new URL(window.location.href);
        const sku = url.searchParams.get('sku');
        return sku ? sku.trim() : '';
      } catch (error) {
        return '';
      }
    }

    async fetchModel() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.setAttribute('aria-busy', 'true');

      try {
        const endpoint = `${this.settings.endpointBase.replace(/\/$/, '')}/${encodeURIComponent(this.sku)}`;
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Language': this.settings.lang
          },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Parts endpoint returned ${response.status}`);
        }

        const data = await response.json();

        if (!data || data.success === false || !data.model) {
          throw new Error('Invalid parts payload');
        }

        this.data = data;
        this.initializeStateFromPayload();
        this.render();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderError(this.settings.errorLabel);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    initializeStateFromPayload() {
      const views = this.getViews();
      const hasCoverImage = Boolean(this.data?.productImage);

      this.state.activeViewMode = views.length ? 'views' : (hasCoverImage ? 'cover' : 'views');
      this.state.activeViewIndex = 0;
      this.state.selectedMarkerNumber = '';
      this.state.selectedPartSku = '';
      this.state.zoom = 1;
      this.state.partsSearch = '';
      this.state.panX = 0;
      this.state.panY = 0;
      this.state.expandedAlternateGroups = {};
    }

    getViews() {
      return Array.isArray(this.data?.views) ? this.data.views : [];
    }

    getActiveView() {
      const views = this.getViews();
      if (!views.length) return null;

      const safeIndex = Math.min(Math.max(this.state.activeViewIndex, 0), views.length - 1);
      return views[safeIndex] || null;
    }

    getCurrentParts() {
      const activeView = this.getActiveView();
      const rawParts = Array.isArray(activeView?.parts) ? activeView.parts : [];
      return this.getVisibleParts(rawParts);
    }

    getCurrentMeta() {
      const activeView = this.getActiveView();
      return activeView?.meta || null;
    }

    getVisibleImageUrl() {
      if (this.state.activeViewMode === 'cover') {
        return this.data?.productImage || '';
      }

      return this.getActiveView()?.image || '';
    }

    getVisibleImageAlt() {
      const model = this.data?.model || {};

      if (this.state.activeViewMode === 'cover') {
        return `${this.getModelTitle(model)} ${this.settings.diagramCoverImageLabel}`;
      }

      return this.getActiveView()?.title || `${this.getModelTitle(model)} ${this.settings.diagramHeading}`;
    }

getMarkerRecords() {
  if (this.state.activeViewMode !== 'views') return [];

  const parts = this.getCurrentParts();

  return parts.reduce((acc, part) => {
    if (!part || !part.hasRealMarker || !part.primaryMarkerNumber) return acc;

    const markerPoints = part.markers?.[part.primaryMarkerNumber];
    const firstPoint = Array.isArray(markerPoints) ? markerPoints[0] : null;

    if (!firstPoint || typeof firstPoint.x !== 'number' || typeof firstPoint.y !== 'number') {
      return acc;
    }

    acc.push({
      markerNumber: String(part.primaryMarkerNumber),
      partSku: part.sku || '',
      partName: part.displayName || part.name || part.sku || '',
      price: this.getDisplayPrice(part),
      x: firstPoint.x,
      y: firstPoint.y,
      width: 5.2,
      height: 5.2,
      isPurchasable: Boolean(part.isPurchasable)
    });

    return acc;
  }, []).sort((a, b) => {
    const aNum = Number(a.markerNumber);
    const bNum = Number(b.markerNumber);

    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
    return a.markerNumber.localeCompare(b.markerNumber);
  });
}

getFilteredParts() {
  const parts = this.getCurrentParts();
  const query = this.state.partsSearch.trim().toLowerCase();

  let filtered = parts;

  if (query) {
    filtered = parts.filter((part) => {
      const partSearchable = [
        part.displayName,
        part.name,
        part.sku,
        part.primaryMarkerNumber,
        Array.isArray(part.markerNumbers) ? part.markerNumbers.join(' ') : ''
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (partSearchable.includes(query)) return true;

      const alternates = this.getAlternateParts(part);

      return alternates.some((alternate) => {
        const alternateSearchable = [
          alternate.displayName,
          alternate.name,
          alternate.sku,
          alternate.primaryMarkerNumber,
          Array.isArray(alternate.markerNumbers) ? alternate.markerNumbers.join(' ') : ''
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return alternateSearchable.includes(query);
      });
    });
  }

  // IMPORTANT: sort marked parts first
  filtered.sort((a, b) => {
    const aMarker = Number(a.primaryMarkerNumber || 0);
    const bMarker = Number(b.primaryMarkerNumber || 0);

    const aIsZero = !aMarker || aMarker === 0;
    const bIsZero = !bMarker || bMarker === 0;

    // marked first
    if (aIsZero && !bIsZero) return 1;
    if (!aIsZero && bIsZero) return -1;

    // numeric order
    return aMarker - bMarker;
  });

  return filtered;
}

    getProductUrl(model) {
      if (!model || !model.handle) return '';
      return `${this.settings.productUrlPrefix || '/products/'}${model.handle}`;
    }

    getPartUrl(part) {
      if (!part || !part.handle) return '';
      return `${this.settings.productUrlPrefix || '/products/'}${part.handle}`;
    }

    getPartImageUrl(part) {
      if (!part) return '';

      return (
        part.image ||
        part.imageUrl ||
        part.featuredImage?.url ||
        part.featured_image?.url ||
        part.image?.url ||
        ''
      );
    }

    isDraftPart(part) {
      if (!part) return false;

      const rawStatus =
        part.status ||
        part.productStatus ||
        part.publishStatus ||
        part.shopifyStatus ||
        part.matchedProductStatus ||
        part.product?.status ||
        '';

      return String(rawStatus).trim().toLowerCase() === 'draft';
    }

    hasAlternateParts(part) {
      return Boolean(part && Array.isArray(part.alternateParts) && part.alternateParts.length);
    }

    shouldDisplayPart(part) {
      return Boolean(part && (!this.isDraftPart(part) || this.hasAlternateParts(part)));
    }

    getVisibleParts(parts = []) {
      if (!Array.isArray(parts)) return [];
      return parts.filter((part) => this.shouldDisplayPart(part));
    }

    getAlternateParts(part) {
  if (!part || !Array.isArray(part.alternateParts)) return [];
  return this.getVisibleParts(part.alternateParts);
}

getVisibleAlternatePartsForParent(part) {
  const alternates = this.getAlternateParts(part);
  const query = this.state.partsSearch.trim().toLowerCase();

  if (!query) return alternates;

  return alternates.filter((alternate) => {
    const searchable = [
      alternate.displayName,
      alternate.name,
      alternate.sku,
      alternate.primaryMarkerNumber,
      Array.isArray(alternate.markerNumbers) ? alternate.markerNumbers.join(' ') : ''
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchable.includes(query);
  });
}

isAlternatePartsExpanded(parentSku) {
  return Boolean(this.state.expandedAlternateGroups?.[parentSku]);
}

toggleAlternateParts(parentSku) {
  if (!parentSku) return;

  this.state.expandedAlternateGroups = {
    ...this.state.expandedAlternateGroups,
    [parentSku]: !this.isAlternatePartsExpanded(parentSku)
  };

  this.renderPartsTable({ scrollToActive: false });
}

 getPartVariantId(part) {
      if (!part) return '';

      const rawVariantId =
        part.variantId ||
        part.variant_id ||
        part.variant?.id ||
        part.matchedVariantId ||
        part.shopifyVariantId ||
        '';

      if (!rawVariantId) return '';

      const stringValue = String(rawVariantId).trim();
      if (!stringValue) return '';

      const gidMatch = stringValue.match(/\/ProductVariant\/(\d+)(\?.*)?$/);
      if (gidMatch && gidMatch[1]) {
        return gidMatch[1];
      }

      if (/^\d+$/.test(stringValue)) {
        return stringValue;
      }

      return '';
    }

    canAddPartToCart(part) {
      if (!part) return false;
      if (this.isDraftPart(part)) return false;

      const variantId = this.getPartVariantId(part);
      if (!variantId) return false;

      return Boolean(part.isPurchasable && part.availableForSale);
    }

findPartBySku(partSku) {
  if (!partSku) return null;

  const parts = this.getCurrentParts();

  for (const part of parts) {
    if (String(part?.sku || '') === String(partSku)) {
      return part;
    }

    const alternates = this.getAlternateParts(part);
    const matchedAlternate = alternates.find(
      (alternate) => String(alternate?.sku || '') === String(partSku)
    );

    if (matchedAlternate) {
      return matchedAlternate;
    }
  }

  return null;
}

    getCartDrawer() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    getCartRoutes() {
      const themeRoutes = window.theme?.routes || {};
      const shopifyRoot = window.Shopify?.routes?.root || '/';
      const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
      const fallbackCartPath = `${normalizedRoot}cart`.replace('//cart', '/cart');
      const fallbackCartAdd = `${normalizedRoot}cart/add.js`.replace('//cart', '/cart');
      const cartPath = themeRoutes.cart || (typeof mainCartUrl === 'string' ? mainCartUrl : fallbackCartPath);
      const cartJson = cartPath.endsWith('.js')
        ? cartPath
        : (typeof cartUrl === 'string' ? cartUrl : `${cartPath}.js`);
      const cartAdd = themeRoutes.cartAdd || (typeof cartAddUrl === 'string' ? cartAddUrl : fallbackCartAdd);
      const normalizedCartPath = cartPath.replace(/\/$/, '');
      const normalizedCurrentPath = window.location.pathname.replace(/\/$/, '');

      return {
        cart: cartPath,
        cartJson,
        cartAdd,
        isCartPage: normalizedCurrentPath === normalizedCartPath
      };
    }

    getCartSections() {
      const cartDrawer = this.getCartDrawer();
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        return cartDrawer.getSectionsToRender()
          .map((section) => section.id)
          .filter(Boolean)
          .join(',');
      }

      const sections = new Set(['cart-icon-bubble']);

      if (cartDrawer) {
        const drawerSection = cartDrawer.closest('.shopify-section');
        if (drawerSection?.id) {
          sections.add(drawerSection.id.replace('shopify-section-', ''));
        }
      }

      return Array.from(sections).join(',');
    }

    setAddToCartButtonState(button, state = 'idle') {
      if (!button) return;

      const defaultLabel = button.dataset.defaultLabel || this.settings.partsAddToCartLabel;
      const addingLabel = this.settings.partsAddingToCartLabel;
      const addedLabel = this.settings.partsAddedToCartLabel;

      button.classList.remove('is-loading', 'is-success');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');

      if (state === 'loading') {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        button.textContent = addingLabel;
        return;
      }

      if (state === 'added') {
        button.classList.add('is-success');
        button.disabled = false;
        button.textContent = addedLabel;
        return;
      }

      button.disabled = false;
      button.textContent = defaultLabel;
    }

    updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response?.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    updateCartCountElements(cart) {
      const itemCount = Number(cart?.item_count);
      if (!Number.isFinite(itemCount)) return;

      if (typeof updateCartCount === 'function') {
        updateCartCount(itemCount);
      }

      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        if (itemCount === 0) {
          element.textContent = '';
          element.style.display = 'none';
        } else if (itemCount < 100) {
          element.textContent = String(itemCount);
          element.style.display = '';
        } else {
          element.textContent = '';
          element.style.display = '';
        }
      });
    }

    refreshCartPreview(cart, response, cartDrawer, routes) {
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && response?.sections) {
        cartDrawer.renderContents(response, false);
      } else if (routes.isCartPage) {
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          if (cartItems.dataset.empty === 'true') {
            window.location.reload();
          } else if (typeof cartItems.refresh === 'function') {
            cartItems.refresh();
          }
        }
      }

      this.updateCartCountElements(cart);
    }

    notifyCartUpdated(cart, lineItem) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          cart,
          lineItem
        }
      }));

      this.updateCartCountElements(cart);
      window.setTimeout(() => {
        this.updateCartCountElements(cart);
      }, 150);
    }

    async addPartToCart(part, button) {
      if (!part || !button) return;
      

      const variantId = this.getPartVariantId(part);
      if (!variantId) return;
      if (!this.canAddPartToCart(part)) return;
      if (button.dataset.loading === 'true') return;

      const cartDrawer = this.getCartDrawer();
      const routes = this.getCartRoutes();

      button.dataset.loading = 'true';
      this.setAddToCartButtonState(button, 'loading');

      try {
        const oldCartResponse = await fetch(routes.cartJson);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', this.getCartSections());

        const response = await fetch(routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status || !response.ok) {
          throw new Error(Array.isArray(error) ? error.join(', ') : (error || this.settings.partsAddToCartErrorLabel));
        }

        this.updateCartIcon(data);

        const newCartResponse = await fetch(routes.cartJson);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        this.refreshCartPreview(newCartData, data, cartDrawer, routes);
        this.notifyCartUpdated(newCartData, data);

        const itemInOldCart = Array.isArray(oldCartData?.items)
          ? oldCartData.items.find((item) => String(item.variant_id) === String(data.variant_id))
          : null;

        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity
                : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }

        this.setAddToCartButtonState(button, 'added');

        window.setTimeout(() => {
          this.setAddToCartButtonState(button, 'idle');
        }, 1400);
      } catch (error) {
        console.error(error);
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error?.message || this.settings.partsAddToCartErrorLabel
          }
        }));

        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          cartDrawer.refresh(true);
        }

        this.setAddToCartButtonState(button, 'idle');
        window.alert(error?.message || this.settings.partsAddToCartErrorLabel);
      } finally {
        delete button.dataset.loading;
      }
    }

    getPlaceholderImageDataUri() {
      const label = this.settings.partsImageUnavailableLabel || 'Image Unavailable';
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="160" height="120" viewBox="0 0 160 120" role="img" aria-label="${label}">
          <rect width="160" height="120" fill="#f3f4f6"/>
          <rect x="1" y="1" width="158" height="118" rx="8" ry="8" fill="none" stroke="#d1d5db"/>
          <path d="M48 76l18-20 14 14 16-18 16 24H48z" fill="#cbd5e1"/>
          <circle cx="62" cy="42" r="8" fill="#cbd5e1"/>
          <text x="80" y="98" text-anchor="middle" font-family="Arial, sans-serif" font-size="11" fill="#6b7280">${label}</text>
        </svg>
      `.trim();

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    }

    getModelTitle(model) {
      if (!model) return '';
      return model.title || model.modelNumber || model.sku || '';
    }

    getModelSubtitle(model) {
      if (!model) return '';
      return model.modelNumber || '';
    }

    getModelImageUrl(model) {
  return (
    this.data?.productImage ||
    model?.image ||
    model?.featuredImage?.url ||
    model?.featured_image?.url ||
    ''
  );
}

    getModelManuals(model) {
  const manualsValue = Array.isArray(model?.manuals)
    ? model.manuals
    : (Array.isArray(this.data?.manuals) ? this.data.manuals : []);

  return manualsValue
    .map((manual) => {
      if (typeof manual === 'string') {
        return {
          url: manual.trim(),
          title: ''
        };
      }

      if (!manual || typeof manual !== 'object') return null;

      const url = String(manual.url || manual.href || manual.file?.url || '').trim();
      const title = String(manual.title || manual.name || '').trim();

      return {
        url,
        title
      };
    })
    .filter((manual) => manual && manual.url);
}

    getModelKeyFeatures() {
  const keyFeatures = this.data?.model?.keyFeatures;
  if (Array.isArray(keyFeatures)) {
    return keyFeatures
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }

  const keyFeaturesHtml = String(keyFeatures || '').trim();
  if (!keyFeaturesHtml) return [];

  const documentRef = this.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!documentRef) {
    return keyFeaturesHtml
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  const template = documentRef.createElement('template');
  template.innerHTML = keyFeaturesHtml;

  const parsedFeatures = Array.from(template.content.querySelectorAll('li'))
    .map((item) => item.textContent.trim())
    .filter(Boolean);

  if (parsedFeatures.length) return parsedFeatures;

  return (template.content.textContent || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

parseSpecifications(specificationsValue) {
  if (!specificationsValue) return [];

  return String(specificationsValue)
    .split(/\s*(?:\r?\n|\|)\s*/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(':');

      if (separatorIndex === -1) {
        return {
          key: '',
          value: line
        };
      }

      const key = line.slice(0, separatorIndex).trim();
      const value = line.slice(separatorIndex + 1).trim();

      return {
        key,
        value
      };
    })
    .filter((item) => item.key || item.value);
}

    getAvailabilityLabel(part) {
      if (!part) return this.settings.partsUnavailableLabel;
      if (this.isDraftPart(part)) return this.settings.partsUnavailableLabel;

      if (part.isPurchasable && part.availableForSale) {
        return this.settings.partsInStockLabel;
      }

      if (part.isMatchedInShopify && !part.availableForSale) {
        return this.settings.partsOutOfStockLabel;
      }

      if (part.isMatchedInShopify && !part.isPurchasable) {
        return this.settings.partsNotSoldLabel;
      }

      return this.settings.partsUnavailableLabel;
    }

    getDisplayPrice(part) {
      return part && !this.isDraftPart(part) ? part.price : null;
    }

    hasDisplayPrice(price) {
      return !(price === null || price === undefined || price === '');
    }

    formatPrice(price) {
      if (price === null || price === undefined || price === '') {
        return '—';
      }

      const numericPrice = Number(price);
      if (!Number.isFinite(numericPrice)) {
        return this.escapeHtml(String(price));
      }

      try {
        return new Intl.NumberFormat('en-CA', {
          style: 'currency',
          currency: 'CAD'
        }).format(numericPrice);
      } catch (error) {
        return `$${numericPrice.toFixed(2)}`;
      }
    }

    getLocale() {
      return String(this.settings?.lang || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
    }

    t(key, replacements = {}) {
      const dictionary = {
        en: {
        partsFinder: 'Parts Finder',
        productImage: 'product image',
        documentFallback: 'Document {index}',
        pdfDocument: 'PDF document',
        viewFallback: 'View {index}',
        partFallback: 'Part',
        specificationFallback: 'Specification',
        sku: 'SKU',
        price: 'Price',
        showLess: 'Show less',
        showMore: 'Show more ({count})',
        alternatePartsAvailable: 'Alternate Parts Available',
        highlightItem: 'Highlight item {item}',
        getNotified: 'Get Notified',
        getNotifiedWhenBack: 'Get notified when {title} is back in stock',
        reloadPage: 'Reload page',
        helpIntro: 'Finding the right replacement part is easy.',
        helpStep1Title: '1. Find your tool model',
        helpStep1Text: 'Use the search bar on the parts finder page to search for your tool model number, SKU, or product name.',
        helpStep1Tip: 'Not sure where to find your model number? Use the "How can I identify my tool?" section on the parts finder page for help locating the product label.',
        helpStep2Title: '2. Select an exploded view',
        helpStep2Text: 'Choose the exploded view below on the diagram that matches the area of your tool you are working on. Each view shows how the tool is assembled and where each part is located.',
        helpStep3Title: '3. Click a part on the diagram',
        helpStep3Text: 'Hover over or click a highlighted part number on the diagram. The matching part will be highlighted in the parts list table below.',
        helpStep3Tip: 'You can also search for a part using the parts search field in the parts table.',
        helpStep4Title: '4. Review part details and purchase',
        helpStep4Text: 'The parts list shows the part name, part number, price, availability, and alternate or replacement parts when available.',
        helpStep4Tip: 'Click Add to Cart to purchase the part or View Part for additional details of the part.',
        helpNeedHelpTitle: 'Need help?',
        helpNeedHelpText: 'If you cannot find your model, part number, or the part you need, please contact our support team with your model number and a photo of your product label.'
      },
      fr: {
        partsFinder: 'Recherche de pièces',
        productImage: 'image du produit',
        documentFallback: 'Document {index}',
        pdfDocument: 'Document PDF',
        viewFallback: 'Vue {index}',
        partFallback: 'Pièce',
        specificationFallback: 'Spécification',
        sku: 'UGS',
        price: 'Prix',
        showLess: 'Afficher moins',
        showMore: 'Afficher plus ({count})',
        alternatePartsAvailable: 'Pièces de remplacement disponibles',
        highlightItem: 'Mettre en évidence l’article {item}',
        getNotified: 'M’aviser',
        getNotifiedWhenBack: 'M’aviser lorsque {title} sera de retour en stock',
        reloadPage: 'Recharger la page',
        helpIntro: 'Trouver la bonne pièce de remplacement est simple.',
        helpStep1Title: '1. Trouvez le modèle de votre outil',
        helpStep1Text: 'Utilisez la barre de recherche de la page du chercheur de pièces pour rechercher le numéro de modèle, le numéro de pièce ou le nom de votre produit.',
        helpStep1Tip: 'Vous ne savez pas où trouver votre numéro de modèle? Consultez la section « Comment puis-je identifier mon outil? » sur la page du chercheur de pièces pour savoir où repérer l’étiquette du produit.',
        helpStep2Title: '2. Sélectionnez une vue éclatée',
        helpStep2Text: 'Choisissez la vue éclatée ci-dessous qui correspond à la partie de votre outil sur laquelle vous travaillez. Chaque vue montre comment l’outil est assemblé et où chaque pièce est située.',
        helpStep3Title: '3. Cliquez sur une pièce dans le schéma',
        helpStep3Text: 'Survolez ou cliquez sur un numéro de pièce mis en évidence dans le schéma. La pièce correspondante sera mise en évidence dans le tableau des pièces ci-dessous.',
        helpStep3Tip: 'Vous pouvez également rechercher une pièce à l’aide du champ de recherche dans le tableau des pièces.',
        helpStep4Title: '4. Consultez les détails de la pièce et effectuez votre achat',
        helpStep4Text: 'La liste des pièces affiche le nom de la pièce, son numéro, son prix, sa disponibilité ainsi que les pièces de rechange ou de remplacement lorsqu’elles sont offertes.',
        helpStep4Tip: 'Cliquez sur « Ajouter au panier » pour acheter la pièce ou sur « Voir la pièce » pour obtenir plus de détails.',
        helpNeedHelpTitle: 'Besoin d’aide?',
        helpNeedHelpText: 'Si vous ne trouvez pas votre modèle, votre numéro de pièce ou la pièce dont vous avez besoin, veuillez communiquer avec notre équipe de soutien en fournissant votre numéro de modèle et une photo de l’étiquette de votre produit.'
      }
    };

  const locale = this.getLocale();
  const template = dictionary[locale]?.[key] || dictionary.en[key] || key;

  return Object.entries(replacements).reduce((value, [name, replacement]) => {
    return value.replaceAll(`{${name}}`, String(replacement ?? ''));
  }, template);
}

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    renderProductInfoContent() {
  const keyFeatures = this.getModelKeyFeatures();
  const specifications = this.parseSpecifications(this.data?.model?.specifications);

  const keyFeaturesHtml = keyFeatures.length
    ? `
      <ul class="parts-pdp__feature-list" role="list">
        ${keyFeatures.map((feature) => `
          <li class="parts-pdp__feature-item">
            <span class="parts-pdp__feature-bullet" aria-hidden="true"></span>
            <span>${this.escapeHtml(feature)}</span>
          </li>
        `).join('')}
      </ul>
    `
    : `<p class="parts-pdp__status-text mb-0">${this.escapeHtml(this.settings.noKeyFeaturesLabel)}</p>`;

  const specificationsHtml = specifications.length
    ? `
      <div class="parts-pdp__spec-list">
        ${specifications.map((item) => `
          <div class="parts-pdp__spec-row">
            <div class="parts-pdp__spec-key">${this.escapeHtml(item.key || this.t('specificationFallback'))}</div>
            <div class="parts-pdp__spec-value">${this.escapeHtml(item.value || '—')}</div>
          </div>
        `).join('')}
      </div>
    `
    : `<p class="parts-pdp__status-text mb-0">${this.escapeHtml(this.settings.noSpecificationsLabel)}</p>`;

  return `
    <div class="parts-pdp__info-sections">
      <section class="parts-pdp__info-section">
        <h3 class="parts-pdp__info-heading">${this.escapeHtml(this.settings.keyFeaturesHeading)}</h3>
        ${keyFeaturesHtml}
      </section>

      <section class="parts-pdp__info-section">
        <h3 class="parts-pdp__info-heading">${this.escapeHtml(this.settings.specificationsHeading)}</h3>
        ${specificationsHtml}
      </section>
    </div>
  `;
}

isProductInfoModalOpen() {
  const modal = this.getProductInfoModal();
  return modal && !modal.hasAttribute('hidden');
}

openProductInfoModal() {
  const modal = this.getProductInfoModal();
  if (!modal) return;

  this.lastActiveElement = document.activeElement;
  this.portalModal(modal);
  modal.removeAttribute('hidden');
  modal.setAttribute('aria-hidden', 'false');
  document.addEventListener('keydown', this.handleKeydown);
  this.lockBodyScroll();

  const closeButton = modal.querySelector('[data-action="close-product-info-modal"]');
  if (closeButton) closeButton.focus();
}

closeProductInfoModal() {
  const modal = this.getProductInfoModal();
  if (!modal) return;

  modal.setAttribute('hidden', '');
  modal.setAttribute('aria-hidden', 'true');
  document.removeEventListener('keydown', this.handleKeydown);
  this.unlockBodyScroll();
  this.restoreModal(modal);

  if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
    this.lastActiveElement.focus();
  }
}

renderHelpModalContent() {
  const steps = [
    ['🔍', this.t('helpStep1Title'), this.t('helpStep1Text'), this.t('helpStep1Tip')],
    ['🖼️', this.t('helpStep2Title'), this.t('helpStep2Text'), ''],
    ['🎯', this.t('helpStep3Title'), this.t('helpStep3Text'), this.t('helpStep3Tip')],
    ['🛒', this.t('helpStep4Title'), this.t('helpStep4Text'), this.t('helpStep4Tip')]
  ];

  return `
    <div class="parts-pdp__help">
      <p class="parts-pdp__help-intro">${this.escapeHtml(this.t('helpIntro'))}</p>

      <ol class="parts-pdp__help-steps" role="list">
        ${steps.map(([icon, title, text, tip]) => `
          <li class="parts-pdp__help-step">
            <span class="parts-pdp__help-icon" aria-hidden="true">${icon}</span>
            <div class="parts-pdp__help-copy">
              <h3 class="parts-pdp__help-heading">${this.escapeHtml(title)}</h3>
              <p class="parts-pdp__help-text">${this.escapeHtml(text)}</p>
              ${tip ? `<p class="parts-pdp__help-tip">${this.escapeHtml(tip)}</p>` : ''}
            </div>
          </li>
        `).join('')}
      </ol>

      <div class="parts-pdp__help-support">
        <h3 class="parts-pdp__help-heading">${this.escapeHtml(this.t('helpNeedHelpTitle'))}</h3>
        <p class="parts-pdp__help-text mb-0">${this.escapeHtml(this.t('helpNeedHelpText'))}</p>
      </div>
    </div>
  `;
}

isHelpModalOpen() {
  const modal = this.getHelpModal();
  return modal && !modal.hasAttribute('hidden');
}

openHelpModal() {
  const modal = this.getHelpModal();
  if (!modal) return;

  this.lastActiveElement = document.activeElement;
  this.portalModal(modal);
  modal.removeAttribute('hidden');
  modal.setAttribute('aria-hidden', 'false');
  document.addEventListener('keydown', this.handleKeydown);
  this.lockBodyScroll();

  const closeButton = modal.querySelector('[data-action="close-help-modal"]');
  if (closeButton) closeButton.focus();
}

closeHelpModal() {
  const modal = this.getHelpModal();
  if (!modal) return;

  modal.setAttribute('hidden', '');
  modal.setAttribute('aria-hidden', 'true');
  document.removeEventListener('keydown', this.handleKeydown);
  this.unlockBodyScroll();
  this.restoreModal(modal);

  if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
    this.lastActiveElement.focus();
  }
}

    render() {
      this.cleanupPortaledModals();

      const model = this.data.model || {};
      const manuals = this.getModelManuals(model);
      const productUrl = this.getProductUrl(model);
      const title = this.getModelTitle(model);
      const subtitle = this.getModelSubtitle(model);
      const rootUrl = window.theme?.routes?.rootUrl || '/';

      const modelImageUrl = this.getModelImageUrl(model) || this.getPlaceholderImageDataUri();
const modelImageAlt = title
  ? `${title} ${this.t('productImage')}`
  : `${model.sku || this.sku} ${this.t('productImage')}`;

const modelThumbHtml = modelImageUrl
  ? `
    <div class="parts-pdp__hero-thumb-wrap">
      <img
        class="parts-pdp__hero-thumb"
        src="${this.escapeHtml(modelImageUrl)}"
        alt="${this.escapeHtml(modelImageAlt)}"
        loading="eager"
        decoding="async"
        width="160"
        height="160"
      >
    </div>
  `
  : '';

      const breadcrumbHtml = this.settings.showBreadcrumbs
        ? `
          <nav class="parts-pdp__breadcrumb mb-6" aria-label="Breadcrumb">
            <ol class="parts-pdp__breadcrumb-list">
              <li>
                <a href="${this.escapeHtml(rootUrl)}">${this.escapeHtml(this.settings.breadcrumbHomeLabel)}</a>
              </li>
              <li class="parts-pdp__breadcrumb-separator" aria-hidden="true">›</li>
              <li>
                <span>${this.escapeHtml(this.t('partsFinder'))}</span>
              </li>
              <li class="parts-pdp__breadcrumb-separator" aria-hidden="true">›</li>
              <li aria-current="page">
                <span>${this.escapeHtml(model.sku || this.sku)}</span>
              </li>
            </ol>
          </nav>
        `
        : '';

      const productInfoTriggerHtml = `
  <button
    type="button"
    class="parts-pdp__jump-button inline-flex items-center gap-3"
    data-action="open-product-info-modal"
    aria-haspopup="dialog"
    aria-controls="PartsPdpProductInfoModal-${this.dataset.sectionId}"
  >
    <span aria-hidden="true">📄</span>
    <span>${this.escapeHtml(this.settings.productInformationLabel)}</span>
  </button>
`;

      const relatedDocumentsTriggerHtml = `
        <button
          type="button"
          class="parts-pdp__jump-button inline-flex items-center gap-3"
          data-action="open-documents-modal"
          aria-haspopup="dialog"
          aria-controls="PartsPdpDocumentsModal-${this.dataset.sectionId}"
          ${manuals.length ? '' : 'hidden'}
        >
          <span aria-hidden="true">📁</span>
          <span>${this.escapeHtml(this.settings.relatedDocumentsLabel)}</span>
        </button>
      `;

      const helpTriggerHtml = `
        <button
          type="button"
          class="parts-pdp__help-button"
          data-action="open-help-modal"
          aria-haspopup="dialog"
          aria-controls="PartsPdpHelpModal-${this.dataset.sectionId}"
        >
          <span class="parts-pdp__help-button-icon" aria-hidden="true">?</span>
          <span>${this.escapeHtml(this.settings.helpLabel)}</span>
        </button>
      `;

      const modalHtml = `
        <div
          id="PartsPdpDocumentsModal-${this.dataset.sectionId}"
          class="parts-pdp__modal"
          hidden
          aria-hidden="true"
        >
          <div class="parts-pdp__modal-overlay" data-documents-overlay></div>

          <div
            class="parts-pdp__modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="PartsPdpDocumentsModalTitle-${this.dataset.sectionId}"
          >
            <div class="parts-pdp__modal-header">
              <h2
                id="PartsPdpDocumentsModalTitle-${this.dataset.sectionId}"
                class="parts-pdp__modal-title"
              >
                ${this.escapeHtml(this.settings.documentsModalTitle)}
              </h2>

              <button
                type="button"
                class="parts-pdp__modal-close"
                data-action="close-documents-modal"
                aria-label="${this.escapeHtml(this.settings.documentsCloseLabel)}"
              >
                ✕
              </button>
            </div>

            <div class="parts-pdp__modal-body">
              ${
                manuals.length
                  ? `
                    <ul class="parts-pdp__docs-list">
                      ${manuals.map((manual, index) => `
                        <li class="parts-pdp__docs-item">
                          <article class="parts-pdp__doc-card">
                            <a
                              class="parts-pdp__doc-link"
                              href="${this.escapeHtml(manual.url || '#')}"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="${this.escapeHtml(this.settings.documentsOpenLabel)}: ${this.escapeHtml(manual.title || this.t('documentFallback', { index: index + 1 }))}"
                            >
                              <span>
                                <span class="parts-pdp__doc-title">${this.escapeHtml(manual.title || this.t('documentFallback', { index: index + 1 }))}</span>
                                <span class="parts-pdp__doc-meta">${this.escapeHtml(this.t('pdfDocument'))}</span>
                              </span>
                              <span class="parts-pdp__doc-arrow" aria-hidden="true">↗</span>
                            </a>
                          </article>
                        </li>
                      `).join('')}
                    </ul>
                  `
                  : `<p class="parts-pdp__status-text">${this.escapeHtml(this.settings.noDocumentsLabel)}</p>`
              }
            </div>
          </div>
        </div>
      `;

      const productInfoModalHtml = `
  <div
    id="PartsPdpProductInfoModal-${this.dataset.sectionId}"
    class="parts-pdp__modal parts-pdp__info-modal"
    hidden
    aria-hidden="true"
  >
    <div class="parts-pdp__modal-overlay" data-product-info-overlay></div>

    <div
      class="parts-pdp__modal-dialog parts-pdp__modal-dialog--info"
      role="dialog"
      aria-modal="true"
      aria-labelledby="PartsPdpProductInfoModalTitle-${this.dataset.sectionId}"
    >
      <div class="parts-pdp__modal-header">
        <h2
          id="PartsPdpProductInfoModalTitle-${this.dataset.sectionId}"
          class="parts-pdp__modal-title"
        >
          ${this.escapeHtml(this.settings.productInfoModalTitle)}
        </h2>

        <button
          type="button"
          class="parts-pdp__modal-close"
          data-action="close-product-info-modal"
          aria-label="${this.escapeHtml(this.settings.productInfoCloseLabel)}"
        >
          ✕
        </button>
      </div>

      <div class="parts-pdp__modal-body">
        ${this.renderProductInfoContent()}
      </div>
    </div>
  </div>
`;

      const helpModalHtml = `
        <div
          id="PartsPdpHelpModal-${this.dataset.sectionId}"
          class="parts-pdp__modal parts-pdp__help-modal"
          hidden
          aria-hidden="true"
        >
          <div class="parts-pdp__modal-overlay" data-help-overlay></div>

          <div
            class="parts-pdp__modal-dialog parts-pdp__modal-dialog--help"
            role="dialog"
            aria-modal="true"
            aria-labelledby="PartsPdpHelpModalTitle-${this.dataset.sectionId}"
          >
            <div class="parts-pdp__modal-header">
              <h2
                id="PartsPdpHelpModalTitle-${this.dataset.sectionId}"
                class="parts-pdp__modal-title"
              >
                ${this.escapeHtml(this.settings.helpModalTitle)}
              </h2>

              <button
                type="button"
                class="parts-pdp__modal-close"
                data-action="close-help-modal"
                aria-label="${this.escapeHtml(this.settings.helpCloseLabel)}"
              >
                ✕
              </button>
            </div>

            <div class="parts-pdp__modal-body">
              ${this.renderHelpModalContent()}
            </div>
          </div>
        </div>
      `;

      this.shell.innerHTML = `
        <div class="parts-pdp__card p-5">
          <div class="parts-pdp__topbar mb-6">
            <div class="parts-pdp__topbar-breadcrumb">
              ${breadcrumbHtml}
            </div>

            <div class="parts-pdp__topbar-help">
              ${helpTriggerHtml}
            </div>
          </div>

          <div class="parts-pdp__top mb-6">
  <div class="parts-pdp__hero">
    ${modelThumbHtml}

    <div class="parts-pdp__main">
      <p class="parts-pdp__meta mb-3">
        ${this.escapeHtml(this.settings.skuLabel)}: ${this.escapeHtml(model.sku || this.sku)}
        ${model.modelNumber ? ` | ${this.escapeHtml(this.settings.modelLabel)}: ${this.escapeHtml(model.modelNumber)}` : ''}
      </p>

      <h1 class="parts-pdp__title mb-0">${this.escapeHtml(title)}</h1>
    </div>
  </div>

  <div class="parts-pdp__actions">
    ${productUrl ? `
      <a
        class="button parts-pdp__product-button"
        href="${this.escapeHtml(productUrl)}"
      >
        ${this.escapeHtml(this.settings.viewProductDetailsLabel)}
      </a>
    ` : ''}
  </div>
</div>

          <div class="parts-pdp__jump-links flex flex-wrap items-center gap-3 pt-4 pb-4">
            ${productInfoTriggerHtml}
            ${relatedDocumentsTriggerHtml}
          </div>
        </div>

        <div class="parts-pdp__diagram-mount mt-6"></div>
        <div class="parts-pdp__parts-mount mt-6"></div>

        ${modalHtml}
        ${productInfoModalHtml}
        ${helpModalHtml}
      `;

      this.diagramMount = this.querySelector('.parts-pdp__diagram-mount');
      this.partsMount = this.querySelector('.parts-pdp__parts-mount');

      this.renderDiagram();
      this.renderPartsTable();
      this.dispatchViewChange();
    }

    renderDiagram() {
      if (!this.diagramMount) return;

      const views = this.getViews();
      const activeView = this.getActiveView();
      const visibleImageUrl = this.getVisibleImageUrl();
      const visibleImageAlt = this.getVisibleImageAlt();
      const markerRecords = this.getMarkerRecords();
      const meta = this.getCurrentMeta();
      const hasViews = views.length > 0;
      const hasCoverImage = Boolean(this.data?.productImage);
      const isViewsMode = this.state.activeViewMode === 'views';
      const canGoPrev = isViewsMode && this.state.activeViewIndex > 0;
      const canGoNext = isViewsMode && this.state.activeViewIndex < views.length - 1;
      const infoText = isViewsMode
        ? (activeView?.title || this.settings.diagramHeading)
        : this.settings.diagramCoverImageLabel;

      const viewModeToggleHtml = `
        <div class="parts-pdp__segment" role="group" aria-label="${this.escapeHtml(this.settings.diagramHeading)}">
          <button
            type="button"
            class="parts-pdp__segment-btn ${isViewsMode ? 'is-active' : ''}"
            data-action="set-view-mode"
            data-view-mode="views"
            ${hasViews ? '' : 'disabled'}
            aria-pressed="${isViewsMode ? 'true' : 'false'}"
          >
            ${this.escapeHtml(this.settings.diagramViewsLabel)}
          </button>

          <button
            type="button"
            class="parts-pdp__segment-btn ${!isViewsMode ? 'is-active' : ''}"
            data-action="set-view-mode"
            data-view-mode="cover"
            ${hasCoverImage ? '' : 'disabled'}
            aria-pressed="${!isViewsMode ? 'true' : 'false'}"
          >
            ${this.escapeHtml(this.settings.diagramCoverImageLabel)}
          </button>
        </div>
      `;

      const viewSelectHtml = isViewsMode && hasViews
  ? `
    <div class="parts-pdp__view-select-wrap mb-4">
      <label
        class="parts-pdp__view-select-label"
        for="PartsPdpViewSelect-${this.dataset.sectionId}"
      >
        ${this.escapeHtml(this.settings.diagramViewsLabel)}
      </label>

      <div class="parts-pdp__view-select-field">
        <select
          id="PartsPdpViewSelect-${this.dataset.sectionId}"
          class="parts-pdp__view-select"
          data-action="select-view-index"
          aria-controls="PartsPdpViewPanel-${this.dataset.sectionId}"
        >
          ${views.map((view, index) => `
            <option
              value="${index}"
              ${index === this.state.activeViewIndex ? 'selected' : ''}
            >
              ${this.escapeHtml(view.title || this.t('viewFallback', { index: index + 1 }))}
            </option>
          `).join('')}
        </select>
      </div>
    </div>
  `
  : '';

      const markerButtonsHtml = isViewsMode && markerRecords.length
  ? markerRecords.map((marker) => {
      const isActive =
        this.state.selectedMarkerNumber === marker.markerNumber ||
        this.state.selectedPartSku === marker.partSku;

      const tooltipId = `PartsPdpMarkerTip-${this.dataset.sectionId}-${String(marker.markerNumber).replace(/[^a-zA-Z0-9_-]/g, '')}`;
const tooltipTitle = marker.partName || this.t('partFallback');
const tooltipSku = marker.partSku || '';
const hasTooltipPrice = this.hasDisplayPrice(marker.price);
const tooltipPrice = hasTooltipPrice ? this.formatPrice(marker.price) : '';
const width = Number(marker.width) || 5.2;
const height = Number(marker.height) || 5.2;

const titleParts = [tooltipTitle];
if (tooltipSku) titleParts.push(`${this.t('sku')}: ${tooltipSku}`);
if (hasTooltipPrice) titleParts.push(`${this.t('price')}: ${tooltipPrice}`);

const ariaLabelParts = [`Part ${marker.markerNumber}: ${tooltipTitle}`];
if (tooltipSku) ariaLabelParts.push(`${this.t('sku')} ${tooltipSku}`);
if (hasTooltipPrice) ariaLabelParts.push(`${this.t('price')} ${tooltipPrice}`);

return `
  <button
    type="button"
    class="parts-pdp__marker ${isActive ? 'is-active' : ''}"
    data-action="select-marker"
    data-marker-number="${this.escapeHtml(marker.markerNumber)}"
    data-part-sku="${this.escapeHtml(marker.partSku)}"
    title="${this.escapeHtml(titleParts.join(' — '))}"
    style="
      left:${marker.x}%;
      top:${marker.y}%;
      width:${width}%;
      height:${height}%;
    "
    aria-label="${this.escapeHtml(ariaLabelParts.join(', '))}"
    aria-pressed="${isActive ? 'true' : 'false'}"
    aria-describedby="${tooltipId}"
  >
    <span class="visually-hidden">${this.escapeHtml(marker.markerNumber)}</span>

    <span
      id="${tooltipId}"
      class="parts-pdp__marker-tooltip"
      role="tooltip"
    >
      <span class="parts-pdp__marker-tooltip-title">${this.escapeHtml(tooltipTitle)}</span>
      ${tooltipSku ? `<span class="parts-pdp__marker-tooltip-sku">${this.escapeHtml(this.t('sku'))}: ${this.escapeHtml(tooltipSku)}</span>` : ''}
      ${hasTooltipPrice ? `<span class="parts-pdp__marker-tooltip-price">${this.escapeHtml(this.t('price'))}: ${this.escapeHtml(tooltipPrice)}</span>` : ''}
    </span>
  </button>
`;
    }).join('')
  : '';

      const emptyStateHtml = !visibleImageUrl
        ? `
          <div class="parts-pdp__diagram-empty">
            <p class="parts-pdp__status-text mb-0">
              ${this.escapeHtml(isViewsMode ? this.settings.diagramNoViewsLabel : this.settings.diagramNoImageLabel)}
            </p>
          </div>
        `
        : `
         <div
          id="PartsPdpViewPanel-${this.dataset.sectionId}"
          class="parts-pdp__viewer-panel"
          role="region"
          aria-label="${this.escapeHtml(infoText)}"
         >
            <div class="parts-pdp__viewer-stage">
              ${isViewsMode && views.length > 1 ? `
                <button
                  type="button"
                  class="parts-pdp__viewer-nav parts-pdp__viewer-nav--prev"
                  data-action="previous-view"
                  ${canGoPrev ? '' : 'disabled'}
                  aria-label="${this.escapeHtml(this.settings.diagramPreviousLabel)}"
                >
                  ‹
                </button>

                <button
                  type="button"
                  class="parts-pdp__viewer-nav parts-pdp__viewer-nav--next"
                  data-action="next-view"
                  ${canGoNext ? '' : 'disabled'}
                  aria-label="${this.escapeHtml(this.settings.diagramNextLabel)}"
                >
                  ›
                </button>
              ` : ''}

              <div class="parts-pdp__viewer-media ${!isViewsMode ? 'parts-pdp__viewer-media--cover' : ''} ${this.state.zoom > 1 ? 'is-zoomed' : ''}"
                  style="
                    --parts-diagram-scale:${this.state.zoom};
                    --parts-diagram-pan-x:${this.state.panX}px;
                    --parts-diagram-pan-y:${this.state.panY}px;
                  "
              >
                <img
                  class="parts-pdp__viewer-image"
                  src="${this.escapeHtml(visibleImageUrl)}"
                  alt="${this.escapeHtml(visibleImageAlt)}"
                  loading="eager"
                  decoding="async"
                  draggable="false"
                >
                ${markerButtonsHtml}
              </div>
            </div>
          </div>
        `;

      this.diagramMount.innerHTML = `
        <div class="parts-pdp__card p-5">
          <div class="parts-pdp__diagram-header mb-4">
            <div>
              <h2 class="parts-pdp__diagram-title mb-1">${this.escapeHtml(this.settings.diagramHeading)}</h2>
              <p class="parts-pdp__diagram-subtitle mb-0">${this.escapeHtml(infoText)}</p>
            </div>

            <div class="parts-pdp__diagram-header-actions">
              ${viewModeToggleHtml}
            </div>
          </div>

          ${viewSelectHtml}

          <div class="parts-pdp__viewer">
            ${emptyStateHtml}
          </div>

          <div class="parts-pdp__viewer-footer pt-4">
            <div class="parts-pdp__viewer-footer-text">
              ${
                isViewsMode && meta
                  ? `
                    <span>${this.escapeHtml(this.settings.diagramPartsCountLabel)}: ${this.escapeHtml(meta.totalParts ?? this.getCurrentParts().length)}</span>
                    <span class="parts-pdp__viewer-footer-separator" aria-hidden="true">•</span>
                    <span>${this.escapeHtml(this.settings.diagramPurchasableCountLabel)}: ${this.escapeHtml(meta.totalPurchasableParts ?? 0)}</span>
                  `
                  : `<span>${this.escapeHtml(this.settings.diagramHintLabel)}</span>`
              }
            </div>

            <div class="parts-pdp__zoom-controls" aria-label="Zoom controls">
              <button
                type="button"
                class="parts-pdp__zoom-btn"
                data-action="zoom-out"
                aria-label="${this.escapeHtml(this.settings.diagramZoomOutLabel)}"
                ${this.state.zoom <= 1 ? 'disabled' : ''}
              >
                −
              </button>

              <button
                type="button"
                class="parts-pdp__zoom-btn"
                data-action="zoom-reset"
                aria-label="${this.escapeHtml(this.settings.diagramResetZoomLabel)}"
              >
                ${Math.round(this.state.zoom * 100)}%
              </button>

              <button
                type="button"
                class="parts-pdp__zoom-btn"
                data-action="zoom-in"
                aria-label="${this.escapeHtml(this.settings.diagramZoomInLabel)}"
                ${this.state.zoom >= 2.4 ? 'disabled' : ''}
              >
                +
              </button>
            </div>
          </div>
        </div>
      `;
    }

getAlternateToggleLabel(isExpanded, hiddenCount) {
  if (isExpanded) return this.t('showLess');
  return this.t('showMore', { count: hiddenCount });
}

renderPartTableRow(part, options = {}) {
  const { rowIsActive = false } = options;

  const rowTitle = part.displayName || part.name || part.sku || '—';
  const markerNumber = part.primaryMarkerNumber || '';
  const isDraft = this.isDraftPart(part);
  const price = this.formatPrice(this.getDisplayPrice(part));
  const availability = this.getAvailabilityLabel(part);
  const partUrl = this.getPartUrl(part);
  const placeholderImage = this.getPlaceholderImageDataUri();
  const partImage = this.getPartImageUrl(part) || placeholderImage;
  const canAddToCart = this.canAddPartToCart(part);
  const variantId = this.getPartVariantId(part);
  const isOutOfStock = Boolean(part.isMatchedInShopify && !part.availableForSale);
  const notifyProductTitle = rowTitle;

  return `
    <tr
      class="parts-pdp__table-row ${rowIsActive ? 'is-active' : ''}"
      data-part-row-sku="${this.escapeHtml(part.sku || '')}"
    >
      <td data-label="${this.escapeHtml(this.settings.partsImageLabel)}">
        <div class="parts-pdp__thumb">
          <img
            class="parts-pdp__thumb-image"
            src="${this.escapeHtml(partImage)}"
            alt="${this.escapeHtml(rowTitle)}"
            loading="lazy"
            decoding="async"
            onerror="this.onerror=null;this.src='${this.escapeHtml(placeholderImage)}';"
          >
        </div>
      </td>

      <td data-label="${this.escapeHtml(this.settings.partsMarkerLabel)}">
        ${markerNumber ? `
          <button
            type="button"
            class="parts-pdp__row-marker"
            data-action="select-part-row"
            data-marker-number="${this.escapeHtml(markerNumber)}"
            data-part-sku="${this.escapeHtml(part.sku || '')}"
            aria-label="Highlight item ${this.escapeHtml(markerNumber)}"
          >
            ${this.escapeHtml(markerNumber)}
          </button>
        ` : '<span class="parts-pdp__table-muted">—</span>'}
      </td>

      <td data-label="${this.escapeHtml(this.settings.partsPartNameLabel)}">
        <span class="parts-pdp__part-title">${this.escapeHtml(rowTitle)}</span>
      </td>

      <td data-label="${this.escapeHtml(this.settings.partsSkuLabel)}">
        <span class="parts-pdp__part-sku">${this.escapeHtml(part.sku || '—')}</span>
      </td>

      <td data-label="${this.escapeHtml(this.settings.partsPriceLabel)}">
        ${this.escapeHtml(price)}
      </td>

      <td data-label="${this.escapeHtml(this.settings.partsAvailabilityLabel)}">
        <span class="parts-pdp__availability-badge ${!isDraft && part.isPurchasable && part.availableForSale ? 'is-success' : ''}">
          ${this.escapeHtml(availability)}
        </span>
      </td>

      <td data-label="${this.escapeHtml(this.settings.partsActionLabel)}">
        <div class="parts-pdp__table-actions">
          ${isDraft ? `
            <span class="parts-pdp__table-muted">${this.escapeHtml(this.settings.partsUnavailableLabel)}</span>
          ` : partUrl ? `
            <a
              class="button secondary-button small-btn parts-pdp__table-btn"
              href="${this.escapeHtml(partUrl)}"
            >
              ${this.escapeHtml(this.settings.partsViewPartLabel)}
            </a>
          ` : `
            <span class="parts-pdp__table-muted">${this.escapeHtml(this.settings.partsUnavailableLabel)}</span>
          `}

          ${canAddToCart ? `
            <button
              type="button"
              class="button small-btn parts-pdp__table-btn parts-pdp__atc-btn"
              data-action="add-part-to-cart"
              data-part-sku="${this.escapeHtml(part.sku || '')}"
              data-variant-id="${this.escapeHtml(variantId)}"
              data-default-label="${this.escapeHtml(this.settings.partsAddToCartLabel)}"
              aria-label="${this.escapeHtml(this.settings.partsAddToCartLabel)}: ${this.escapeHtml(rowTitle)}"
            >
              ${this.escapeHtml(this.settings.partsAddToCartLabel)}
            </button>
          ` : ''}

          ${/*
          !canAddToCart && isOutOfStock ? `
            <button
              class="button small-btn js-gwt-notify-trigger parts-pdp__table-btn parts-pdp__table-notify"
              name="get-notified"
              aria-haspopup="dialog"
              data-product="${this.escapeHtml(notifyProductTitle)}"
              data-sku="${this.escapeHtml(part.sku || '')}"
              aria-label="${this.escapeHtml(this.t('getNotifiedWhenBack', { title: rowTitle }))}"
            >
              ${this.escapeHtml(this.t('getNotified'))}
            </button>
          ` : ''
          */ ''}
        </div>
      </td>
    </tr>
  `;
}

renderAlternatePartCard(part, parentSku) {
  const rowTitle = part.displayName || part.name || part.sku || '—';
  const markerNumber = part.primaryMarkerNumber || '';
  const isDraft = this.isDraftPart(part);
  const price = this.formatPrice(this.getDisplayPrice(part));
  const availability = this.getAvailabilityLabel(part);
  const partUrl = this.getPartUrl(part);
  const placeholderImage = this.getPlaceholderImageDataUri();
  const partImage = this.getPartImageUrl(part) || placeholderImage;
  const canAddToCart = this.canAddPartToCart(part);
  const variantId = this.getPartVariantId(part);
  const isOutOfStock = Boolean(part.isMatchedInShopify && !part.availableForSale);
  const notifyProductTitle = rowTitle;

  const isActive =
    this.state.selectedPartSku === String(part?.sku || '') ||
    this.state.selectedMarkerNumber === String(part?.primaryMarkerNumber || '');

  return `
    <article
      class="parts-pdp__alternate-card ${isActive ? 'is-active' : ''}"
      data-part-row-sku="${this.escapeHtml(part.sku || '')}"
      data-parent-part-sku="${this.escapeHtml(parentSku)}"
    >
      <div class="parts-pdp__alternate-card-top">
        <div class="parts-pdp__alternate-thumb-wrap">
          <img
            class="parts-pdp__alternate-thumb"
            src="${this.escapeHtml(partImage)}"
            alt="${this.escapeHtml(rowTitle)}"
            loading="lazy"
            decoding="async"
            onerror="this.onerror=null;this.src='${this.escapeHtml(placeholderImage)}';"
          >
        </div>

        <div class="parts-pdp__alternate-main">
          <div class="parts-pdp__alternate-title-row">
            <div class="parts-pdp__alternate-copy">
              <p class="parts-pdp__alternate-title">${this.escapeHtml(rowTitle)}</p>
              <p class="parts-pdp__alternate-sku">SKU: ${this.escapeHtml(part.sku || '—')}</p>
            </div>

            <div class="parts-pdp__alternate-meta">
              <p class="parts-pdp__alternate-price">${this.escapeHtml(price)}</p>
              <span class="parts-pdp__alternate-availability ${!isDraft && part.isPurchasable && part.availableForSale ? 'is-success' : ''}">
                ${this.escapeHtml(availability)}
              </span>
            </div>
          </div>

          <div class="parts-pdp__alternate-aux">
            ${markerNumber ? `
              <button
                type="button"
                class="parts-pdp__alternate-marker"
                data-action="select-part-row"
                data-marker-number="${this.escapeHtml(markerNumber)}"
                data-part-sku="${this.escapeHtml(part.sku || '')}"
                aria-label="Highlight item ${this.escapeHtml(markerNumber)}"
              >
                ${this.escapeHtml(this.settings.partsMarkerLabel)} ${this.escapeHtml(markerNumber)}
              </button>
            ` : ''}
          </div>

          <div class="parts-pdp__alternate-actions">
            ${isDraft ? `
              <span class="parts-pdp__table-muted">${this.escapeHtml(this.settings.partsUnavailableLabel)}</span>
            ` : partUrl ? `
              <a
                class="button secondary-button small-btn parts-pdp__table-btn"
                href="${this.escapeHtml(partUrl)}"
              >
                ${this.escapeHtml(this.settings.partsViewPartLabel)}
              </a>
            ` : ''}

            ${canAddToCart ? `
              <button
                type="button"
                class="button small-btn parts-pdp__table-btn parts-pdp__atc-btn"
                data-action="add-part-to-cart"
                data-part-sku="${this.escapeHtml(part.sku || '')}"
                data-variant-id="${this.escapeHtml(variantId)}"
                data-default-label="${this.escapeHtml(this.settings.partsAddToCartLabel)}"
                aria-label="${this.escapeHtml(this.settings.partsAddToCartLabel)}: ${this.escapeHtml(rowTitle)}"
              >
                ${this.escapeHtml(this.settings.partsAddToCartLabel)}
              </button>
            ` : ''}

            ${/*
            !canAddToCart && isOutOfStock ? `
              <button
                class="button small-btn js-gwt-notify-trigger parts-pdp__table-btn parts-pdp__table-notify"
                name="get-notified"
                aria-haspopup="dialog"
                data-product="${this.escapeHtml(notifyProductTitle)}"
                data-sku="${this.escapeHtml(part.sku || '')}"
                aria-label="Get notified when ${this.escapeHtml(rowTitle)} is back in stock"
              >
                Get Notified
              </button>
            ` : ''
            */ ''}
          </div>
        </div>
      </div>
    </article>
  `;
}

renderPartRowsWithAlternates(part) {
  const parentSku = String(part?.sku || '');
  const parentIsActive =
    this.state.selectedPartSku === parentSku ||
    this.state.selectedMarkerNumber === String(part?.primaryMarkerNumber || '');

  let rowsHtml = this.renderPartTableRow(part, {
    rowIsActive: parentIsActive
  });

  const alternates = this.getVisibleAlternatePartsForParent(part);
  if (!alternates.length) return rowsHtml;

  const isExpanded = this.isAlternatePartsExpanded(parentSku);
  const defaultVisibleCount = 2;
  const visibleAlternates = isExpanded ? alternates : alternates.slice(0, defaultVisibleCount);

  const hiddenCount = Math.max(0, alternates.length - defaultVisibleCount);

  rowsHtml += `
    <tr class="parts-pdp__alternate-panel-row" data-parent-part-sku="${this.escapeHtml(parentSku)}">
      <td colspan="7">
        <div class="parts-pdp__alternate-panel">
          <div class="parts-pdp__alternate-panel-rail" aria-hidden="true"></div>

          <div class="parts-pdp__alternate-panel-content">
            <div class="parts-pdp__alternate-panel-header">
              <p class="parts-pdp__alternate-panel-title">${this.escapeHtml(this.t('alternatePartsAvailable'))}</p>
            </div>

            <div class="parts-pdp__alternate-grid">
              ${visibleAlternates.map((alternate) => this.renderAlternatePartCard(alternate, parentSku)).join('')}
            </div>

            ${alternates.length > defaultVisibleCount ? `
              <div class="parts-pdp__alternate-panel-footer">
                <button
                  type="button"
                  class="parts-pdp__alternate-toggle"
                  data-action="toggle-alternate-parts"
                  data-parent-sku="${this.escapeHtml(parentSku)}"
                  aria-expanded="${isExpanded ? 'true' : 'false'}"
                >
                  ${this.escapeHtml(this.getAlternateToggleLabel(isExpanded, hiddenCount))}
                </button>
              </div>
            ` : ''}
          </div>
        </div>
      </td>
    </tr>
  `;

  return rowsHtml;
}

    renderPartsTable(options = {}) {
      if (!this.partsMount) return;

      const {
        preserveFocus = false,
        focusValue = this.state.partsSearch,
        selectionStart = null,
        selectionEnd = null,
        scrollToActive = false
      } = options;

      const isViewsMode = this.state.activeViewMode === 'views';
      const allParts = isViewsMode ? this.getCurrentParts() : [];
      const filteredParts = isViewsMode ? this.getFilteredParts() : [];
      const hasSearch = this.state.partsSearch.trim().length > 0;
      const emptyMessage = hasSearch ? this.settings.partsEmptySearchLabel : this.settings.partsEmptyLabel;
      const placeholderImage = this.getPlaceholderImageDataUri();
      

      this.partsMount.innerHTML = `
        <div class="parts-pdp__card p-5">
          <div class="parts-pdp__parts-header mb-4">
            <div>
              <h2 class="parts-pdp__diagram-title mb-1">${this.escapeHtml(this.settings.partsHeading)}</h2>
              <p class="parts-pdp__diagram-subtitle mb-0">
                ${isViewsMode ? `${filteredParts.length} ${this.escapeHtml(this.settings.partsResultsLabel)}` : this.escapeHtml(this.settings.diagramHintLabel)}
              </p>
            </div>

            <div class="parts-pdp__parts-search-wrap">
              <label class="visually-hidden" for="PartsPdpSearch-${this.dataset.sectionId}">
                ${this.escapeHtml(this.settings.partsSearchPlaceholder)}
              </label>
              <div class="parts-pdp__search-field">
                <input
                  id="PartsPdpSearch-${this.dataset.sectionId}"
                  class="parts-pdp__search-input"
                  type="search"
                  value="${this.escapeHtml(this.state.partsSearch)}"
                  placeholder="${this.escapeHtml(this.settings.partsSearchPlaceholder)}"
                  data-action="filter-parts"
                  autocapitalize="off"
                  autocomplete="off"
                  autocorrect="off"
                  spellcheck="false"
                  ${isViewsMode ? '' : 'disabled'}
                >
                ${hasSearch ? `
                  <button
                    type="button"
                    class="parts-pdp__search-clear"
                    data-action="clear-parts-search"
                    aria-label="${this.escapeHtml(this.settings.partsClearSearchLabel)}"
                  >
                    ✕
                  </button>
                ` : ''}
              </div>
            </div>
          </div>

          ${
            !isViewsMode
              ? `
                <div class="parts-pdp__parts-empty">
                  <p class="parts-pdp__status-text mb-0">${this.escapeHtml(this.settings.diagramHintLabel)}</p>
                </div>
              `
              : allParts.length === 0 || filteredParts.length === 0
                ? `
                  <div class="parts-pdp__parts-empty">
                    <p class="parts-pdp__status-text mb-0">${this.escapeHtml(emptyMessage)}</p>
                  </div>
                `
                : `
                  <div class="parts-pdp__table-wrap" aria-label="${this.escapeHtml(this.settings.partsHeading)}">
                    <table class="parts-pdp__table">
                      <thead>
                        <tr>
                          <th scope="col">${this.escapeHtml(this.settings.partsImageLabel)}</th>
                          <th scope="col">${this.escapeHtml(this.settings.partsMarkerLabel)}</th>
                          <th scope="col">${this.escapeHtml(this.settings.partsPartNameLabel)}</th>
                          <th scope="col">${this.escapeHtml(this.settings.partsSkuLabel)}</th>
                          <th scope="col">${this.escapeHtml(this.settings.partsPriceLabel)}</th>
                          <th scope="col">${this.escapeHtml(this.settings.partsAvailabilityLabel)}</th>
                          <th scope="col">${this.escapeHtml(this.settings.partsActionLabel)}</th>
                        </tr>
                      </thead>
                      <tbody>

                        ${filteredParts.map((part) => this.renderPartRowsWithAlternates(part)).join('')}
                      </tbody>
                    </table>
                  </div>
                `
          }
        </div>
      `;

      if (preserveFocus) {
        const input = this.partsMount.querySelector('[data-action="filter-parts"]');
        if (input) {
          requestAnimationFrame(() => {
            input.focus();
            input.value = focusValue;

            if (
              typeof selectionStart === 'number' &&
              typeof selectionEnd === 'number' &&
              typeof input.setSelectionRange === 'function'
            ) {
              input.setSelectionRange(selectionStart, selectionEnd);
            }
          });
        }
      }

      if (scrollToActive) {
        this.scrollActiveRowIntoView();
      }
    }

    renderError(message) {
      this.shell.innerHTML = `
        <div class="parts-pdp__error card p-6" role="status" aria-live="polite">
          <p class="parts-pdp__status-text mb-3">${this.escapeHtml(message)}</p>
          <p class="parts-pdp__status-text mb-0">
            <a href="${window.location.pathname}">${this.escapeHtml(this.t('reloadPage'))}</a>
          </p>
        </div>
      `;
    }

    setActiveViewMode(mode) {
      const hasViews = this.getViews().length > 0;
      const hasCoverImage = Boolean(this.data?.productImage);

      if (mode === 'views' && !hasViews) return;
      if (mode === 'cover' && !hasCoverImage) return;
      if (this.state.activeViewMode === mode) return;

      this.state.activeViewMode = mode;
      this.state.selectedMarkerNumber = '';
      this.state.selectedPartSku = '';
      this.state.zoom = 1;
      this.state.partsSearch = '';

      this.resetPan();
      this.renderDiagram();
      this.renderPartsTable({ scrollToActive: false });
      this.dispatchViewChange();
    }

    setActiveViewIndex(index) {
      const views = this.getViews();
      if (!views.length) return;

      const safeIndex = Math.min(Math.max(index, 0), views.length - 1);
      if (safeIndex === this.state.activeViewIndex && this.state.activeViewMode === 'views') return;

      this.state.activeViewMode = 'views';
      this.state.activeViewIndex = safeIndex;
      this.state.selectedMarkerNumber = '';
      this.state.selectedPartSku = '';
      this.state.zoom = 1;
      this.state.partsSearch = '';

      this.resetPan();
      this.renderDiagram();
      this.renderPartsTable({ scrollToActive: false });
      this.dispatchViewChange();
    }

    goToRelativeView(delta) {
      const views = this.getViews();
      if (this.state.activeViewMode !== 'views' || views.length < 2) return;

      const nextIndex = this.state.activeViewIndex + delta;
      if (nextIndex < 0 || nextIndex > views.length - 1) return;

      this.setActiveViewIndex(nextIndex);
    }

    selectMarker(markerNumber, partSku, options = {}) {
      this.state.selectedMarkerNumber = String(markerNumber || '');
      this.state.selectedPartSku = String(partSku || '');

      this.renderDiagram();
      this.renderPartsTable({ scrollToActive: !options.fromTable });
      this.dispatchViewChange();
    }

setZoom(nextZoom) {
  const safeZoom = Math.min(Math.max(Number(nextZoom) || 1, 1), 2.4);
  if (safeZoom === this.state.zoom) return;

  this.state.zoom = safeZoom;

  if (safeZoom <= 1) {
    this.resetPan();
  } else {
    const clamped = this.clampPan(this.state.panX, this.state.panY);
    this.state.panX = clamped.x;
    this.state.panY = clamped.y;
  }

  this.renderDiagram();
}

    resetPan() {
  this.state.panX = 0;
  this.state.panY = 0;
}

clampPan(panX, panY) {
  const viewer = this.querySelector('.parts-pdp__viewer-stage');
  const media = this.querySelector('.parts-pdp__viewer-media');
  const image = this.querySelector('.parts-pdp__viewer-image');

  if (!viewer || !media || !image || this.state.zoom <= 1) {
    return { x: 0, y: 0 };
  }

  const viewerRect = viewer.getBoundingClientRect();

  // Use the unscaled rendered size of the image/media, not the already-transformed rect.
  const baseWidth = image.offsetWidth || media.offsetWidth;
  const baseHeight = image.offsetHeight || media.offsetHeight;

  const scaledWidth = baseWidth * this.state.zoom;
  const scaledHeight = baseHeight * this.state.zoom;

  const maxOffsetX = Math.max(0, (scaledWidth - viewerRect.width) / 2);
  const maxOffsetY = Math.max(0, (scaledHeight - viewerRect.height) / 2);

  return {
    x: Math.max(-maxOffsetX, Math.min(maxOffsetX, panX)),
    y: Math.max(-maxOffsetY, Math.min(maxOffsetY, panY))
  };
}

applyPan(nextPanX, nextPanY) {
  const clamped = this.clampPan(nextPanX, nextPanY);
  this.state.panX = clamped.x;
  this.state.panY = clamped.y;
}

handlePointerDown(event) {
  const stage = event.target.closest('.parts-pdp__viewer-stage');
  const media = this.querySelector('.parts-pdp__viewer-media');

  if (!stage || !media) return;
  if (this.state.zoom <= 1) return;
  if (event.button !== 0) return;

  const interactiveTarget = event.target.closest(
    '[data-action="select-marker"], [data-action="previous-view"], [data-action="next-view"], [data-action="zoom-in"], [data-action="zoom-out"], [data-action="zoom-reset"], a, button:not(.parts-pdp__marker)'
  );

  if (interactiveTarget && !event.target.closest('.parts-pdp__viewer-image')) {
    return;
  }

  this.pointerState.isPointerDown = true;
  this.pointerState.pointerId = event.pointerId;
  this.pointerState.startX = event.clientX;
  this.pointerState.startY = event.clientY;
  this.pointerState.startPanX = this.state.panX;
  this.pointerState.startPanY = this.state.panY;
  this.pointerState.moved = false;

  event.preventDefault();

  if (stage.setPointerCapture) {
    try {
      stage.setPointerCapture(event.pointerId);
    } catch (error) {
      // no-op
    }
  }

  this.classList.add('is-panning');
}

handlePointerMove(event) {
  if (!this.pointerState.isPointerDown) return;
  if (event.pointerId !== this.pointerState.pointerId) return;
  if (this.state.zoom <= 1) return;

  const deltaX = event.clientX - this.pointerState.startX;
  const deltaY = event.clientY - this.pointerState.startY;

  if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
    this.pointerState.moved = true;
  }

  event.preventDefault();

  this.applyPan(
    this.pointerState.startPanX + deltaX,
    this.pointerState.startPanY + deltaY
  );

  this.updatePanStyle();
}

handlePointerUp(event) {
  if (!this.pointerState.isPointerDown) return;
  if (event.pointerId !== this.pointerState.pointerId) return;

  const stage = this.querySelector('.parts-pdp__viewer-stage');

  if (stage && stage.releasePointerCapture) {
    try {
      stage.releasePointerCapture(event.pointerId);
    } catch (error) {
      // no-op
    }
  }

  this.pointerState.isPointerDown = false;
  this.pointerState.pointerId = null;

  this.classList.remove('is-panning');

  window.setTimeout(() => {
    this.pointerState.moved = false;
  }, 0);
}

handlePointerCancel(event) {
  if (!this.pointerState.isPointerDown) return;
  if (event.pointerId !== this.pointerState.pointerId) return;

  const stage = this.querySelector('.parts-pdp__viewer-stage');

  if (stage && stage.releasePointerCapture) {
    try {
      stage.releasePointerCapture(event.pointerId);
    } catch (error) {
      // no-op
    }
  }

  this.pointerState.isPointerDown = false;
  this.pointerState.pointerId = null;

  this.classList.remove('is-panning');
}

updatePanStyle() {
  const media = this.querySelector('.parts-pdp__viewer-media');
  if (!media) return;

  media.style.setProperty('--parts-diagram-pan-x', `${this.state.panX}px`);
  media.style.setProperty('--parts-diagram-pan-y', `${this.state.panY}px`);
}

    dispatchViewChange() {
      const detail = {
        sku: this.sku,
        mode: this.state.activeViewMode,
        activeViewIndex: this.state.activeViewIndex,
        activeView: this.getActiveView(),
        parts: this.getCurrentParts(),
        selectedMarkerNumber: this.state.selectedMarkerNumber,
        selectedPartSku: this.state.selectedPartSku
      };

      this.dispatchEvent(new CustomEvent('parts-finder:view-change', {
        detail,
        bubbles: true
      }));
    }

scrollActiveRowIntoView() {
  if (!this.partsMount || !this.state.selectedPartSku || typeof CSS === 'undefined' || !CSS.escape) return;

  const activeRow = this.partsMount.querySelector(
    `[data-part-row-sku="${CSS.escape(this.state.selectedPartSku)}"]`
  );
  if (!activeRow) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = prefersReducedMotion ? 'auto' : 'smooth';

  if (typeof activeRow.scrollIntoView === 'function') {
    activeRow.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior
    });
  }
}

    isDocumentsModalOpen() {
      const modal = this.getDocumentsModal();
      return modal && !modal.hasAttribute('hidden');
    }

    openDocumentsModal() {
      const modal = this.getDocumentsModal();
      if (!modal) return;

      this.lastActiveElement = document.activeElement;
      this.portalModal(modal);
      modal.removeAttribute('hidden');
      modal.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this.handleKeydown);
      this.lockBodyScroll();

      const closeButton = modal.querySelector('[data-action="close-documents-modal"]');
      if (closeButton) closeButton.focus();
    }

    closeDocumentsModal() {
      const modal = this.getDocumentsModal();
      if (!modal) return;

      modal.setAttribute('hidden', '');
      modal.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.handleKeydown);
      this.unlockBodyScroll();
      this.restoreModal(modal);

      if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
        this.lastActiveElement.focus();
      }
    }

    lockBodyScroll() {
      document.documentElement.style.overflow = 'hidden';
    }

    unlockBodyScroll() {
      document.documentElement.style.overflow = '';
    }
  }

  customElements.define('parts-model-pdp', PartsModelPdp);
}
